import SideModal from "components/SideModal";
import { useAuth } from "contexts/AuthContext";
import { useTranslation } from "contexts/i18nContext";
import { useToast } from "contexts/ToastContext";
import React, { ChangeEvent, useEffect, useState } from "react";
import {
  editCourtAppeal,
  fetchAssignedProsecutor,
} from "services/prosecutorDecipilineService";
import { AssignedProsecutor, Complaints, CourtAppeal, Prosecutor } from "types";
import { LoadingIcon } from "utils/constant";
import {
  ACCUSED_COMPLAINT_STATUS,
  COURT_APPEAL_STATUS,
} from "utils/constantValues";
import { ROLES } from "utils/roles";

interface DecisionModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  summonData: CourtAppeal;
  complaintData: Complaints;
  onRefresh: any;
  onComplaintsRefresh: any;
}

export default function CourtSummonConfirmationModal({
  isOpen,
  setIsOpen,
  complaintData,
  summonData,
  onRefresh,
  onComplaintsRefresh,
}: DecisionModalProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  // const [error, setError] = useState<string | null>(null);
  const [prosecutorsData, setProsecutorsData] = useState<AssignedProsecutor[]>(
    [],
  );
  // const [showDropdown, setShowDropdown] = useState(false);

  const [formState, setFormState] = useState({
    assigned_prosecutor_id: "",
    assigned_prosecutor: null as Prosecutor | null,
    status: COURT_APPEAL_STATUS.ASSIGNED_TO_PROSECUTOR,
  });

  useEffect(() => {
    const loadProsecutors = async () => {
      try {
        const data = await fetchAssignedProsecutor(
          user?.admin_unit,
          user?.org_unit,
          user?.access,
        );

        setProsecutorsData(data);
        // setError("");
      } catch (err) {
        // setError("Failed to load prosecutors.");
      } finally {
        setLoading(false);
      }
    };
    loadProsecutors();
  }, [user]);

  const filtered = prosecutorsData?.filter(
    (p) =>
      p?.roles?.includes(ROLES.INSPECTION_PROSECUTOR) &&
      `${p?.user?.first_name} ${p?.user?.last_name}`
        .toLowerCase()
        .includes(query.toLowerCase()),
  );

  const handleSelect = (prosecutor: Prosecutor, name: string) => {
    setFormState((prevState) => ({
      ...prevState,
      [name]: prosecutor,
    }));
    setQuery("");
    // setShowDropdown(false);
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    if (name === "decision_type") {
      setFormState((prev) => ({ ...prev, assigned_prosecutor: null }));
    }

    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!summonData) return;

    setLoading(true);

    const data = new FormData();
    data.append(
      "user_id_assigned_prosecutor",
      formState?.assigned_prosecutor && formState?.assigned_prosecutor?.id
        ? String(formState.assigned_prosecutor.id)
        : "",
    );
    data.append(
      "assigned_prosecutor",
      `${formState?.assigned_prosecutor?.first_name || ""} ${
        formState?.assigned_prosecutor?.last_name || ""
      }`,
    );
    data.append("status", formState.status ?? "");

    const summon_id = summonData?.id || "";
    const isFormData = true;

    try {
      await editCourtAppeal(data, summon_id, isFormData);

      onRefresh();
      onComplaintsRefresh();
      setIsOpen(false);
      showToast(t("confirmed"), "success");
      setFormState({
        assigned_prosecutor_id: "",
        assigned_prosecutor: null as Prosecutor | null,
        status: ACCUSED_COMPLAINT_STATUS.ASSIGNED_TO_PROSECUTOR,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("confirmCourtSummon")}
    >
      <form onSubmit={handleSubmit} className="evidence-form">
        <section className="evidence-section">
          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              {t("assignProsecutor")}
            </label>
            <select
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              name="assigned_prosecutor_id"
              value={formState.assigned_prosecutor_id}
              onChange={(e) => {
                handleChange(e);
                const selected = filtered?.find(
                  (p) => p.user.id?.toString() === e.target.value?.toString(),
                );

                if (selected && selected.user) {
                  handleSelect(selected.user, "assigned_prosecutor");
                }
              }}
            >
              <option value={""}>{t("selectProsecutor")}</option>
              {filtered?.map((p) => (
                <option key={p.user.id} value={p.user.id}>
                  {p.user?.first_name} ({p.user?.last_name})
                </option>
              ))}
            </select>
          </div>
        </section>

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}
            </div>
          ) : (
            t("assign")
          )}
        </button>
      </form>
    </SideModal>
  );
}
